import { Outlet, NavLink } from 'react-router-dom';
import { usePermissions } from '@/features/auth/hooks/useAuth';
import { Shield } from 'lucide-react';
import { LogoutButton } from '@/features/auth/components/LogoutButton';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { cn } from '@/lib/cn';

/**
 * Sidebar del panel admin (refactor v2).
 *
 * Menú top-level:
 *   - Inicio            `/`
 *   - Gestión Académica `/gestion-academica`
 *   - Auditoría         `/auditoria`
 *   - Configuración     `/configuracion`  (visible si tiene permiso de
 *                          configuración o permiso de cualquier sub-sección)
 *
 * Cada entrada usa `end={path === '/'}` para que `/` solo esté activo en la
 * raíz exacta y no en subrutas.
 */

interface MenuItem {
  path: string;
  label: string;
  /**
   * Permiso(s) requerido(s) para ver el item:
   * - `null` → visible para cualquier usuario autenticado (no gated).
   * - `'*'` → solo administradores (wildcard).
   * - `string` → requiere ese permiso.
   * - `string[]` → requiere al menos uno.
   */
  permiso: string | string[] | null;
}

const MENU: MenuItem[] = [
  { path: '/', label: 'Inicio', permiso: null },
  { path: '/gestion-academica', label: 'Gestión Académica', permiso: ['programa.leer', 'cohorte.leer'] },
  { path: '/auditoria', label: 'Auditoría', permiso: 'audit.leer' },
  {
    path: '/configuracion',
    label: 'Configuración',
    permiso: ['permiso.gestionar', 'configuracion.gestionar'],
  },
];

function usuarioPuede(item: MenuItem, permisos: Set<string>): boolean {
  // Si no requiere permisos (`null`), el usuario solo necesita estar autenticado
  // (RequireAuth ya garantiza que llega aquí con sesión válida).
  if (item.permiso === null) return true;
  // Admin con '*' ve todo.
  if (permisos.has('*')) return true;
  const requerido = Array.isArray(item.permiso) ? item.permiso : [item.permiso];
  return requerido.some((p) => permisos.has(p));
}

export function AppShell() {
  const permisos = usePermissions();
  const { user } = useAuth();
  const items = MENU.filter((m) => usuarioPuede(m, permisos));

  return (
    <div className="grid min-h-screen grid-cols-[240px_1fr] bg-bg text-text-primary">
      <aside className="border-r border-border bg-bg-elevated">
        <div className="flex h-16 items-center gap-3 border-b border-border px-4">
          <img src="/logo_izquierda.png" alt="Logo izquierda" className="h-10 w-auto object-contain" />
          <img src="/logo_derecha.png" alt="Logo derecha" className="h-10 w-auto object-contain" />
        </div>
        <nav className="px-2 py-4">
          <ul className="space-y-1">
            {items.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    cn(
                      'block rounded-md px-3 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground',
                      isActive && 'bg-accent text-accent-foreground',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      <div className="flex flex-col">
        <header className="flex h-16 items-center justify-between border-b border-border bg-bg-elevated px-6">
          <div className="text-sm text-text-secondary">
            {user && <span>{user.email}</span>}
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            {user?.actor_tipo === 'usuario' && (
              <span title="Sesión de usuario" aria-label="Sesión de usuario">
                <Shield className="h-5 w-5 text-text-secondary" />
              </span>
            )}
            <LogoutButton />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
